<template>
  <div class="border-t border-white/5 p-2 relative">
    <!-- Muted banner (user has an active MuteRecord) -->
    <div
      v-if="muted"
      class="mx-1 mb-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20 text-[11px] text-red-200"
    >
      <p class="font-semibold">{{ $t('chat.errors.muted', { duration: muteDurationLabel }) }}</p>
      <p v-if="myMute?.reason" class="text-red-300/80 mt-0.5">"{{ myMute.reason }}"</p>
    </div>

    <!-- Quote-reply preview — visible when the user has clicked Reply
         on a message and hasn't sent / cancelled yet. The id is sent
         along with submit() and the renderer pairs it with the source
         message via replyToPreview. -->
    <div
      v-if="replyTo"
      class="mx-1 mb-2 px-2 py-1.5 rounded-md bg-primary/[0.06] border-l-2 border-primary/60 flex items-center gap-2"
    >
      <div class="min-w-0 flex-1">
        <p class="flex items-center gap-1 text-[11px] font-semibold leading-4 text-primary min-w-0">
          <NuxtIcon name="mdi:reply" class="shrink-0 text-[12px]" aria-hidden="true" />
          <span class="truncate">{{ $t('chat.reply.replying_to', { name: replyTo.authorName }) }}</span>
        </p>
        <p class="text-xs leading-4 text-text-muted line-clamp-2 [overflow-wrap:anywhere]">{{ replyTo.bodySnippet || '—' }}</p>
      </div>
      <button
        type="button"
        class="shrink-0 p-1 rounded text-text-muted hover:text-white hover:bg-white/[0.04]"
        :title="$t('chat.reply.cancel')"
        :aria-label="$t('chat.reply.cancel')"
        @click="$emit('cancel-reply')"
      >
        <el-icon size="12"><Close /></el-icon>
      </button>
    </div>

    <!-- Upload chips -->
    <div v-if="uploadList.length > 0" class="flex gap-2 flex-wrap mb-2 px-1">
      <div
        v-for="u in uploadList"
        :key="u.clientFileId"
        class="relative w-16 h-16 rounded-lg overflow-hidden bg-black/30 border border-white/5 shrink-0 group"
      >
        <img alt="" :src="u.localPreviewUrl" class="w-full h-full object-cover" />

        <!-- Progress ring overlay while uploading -->
        <div
          v-if="u.state === 'uploading' || u.state === 'queued'"
          class="absolute inset-0 bg-black/60 flex items-center justify-center text-[10px] text-white font-bold"
        >
          {{ u.progress }}%
        </div>

        <!-- Error overlay -->
        <div
          v-else-if="u.state === 'error'"
          class="absolute inset-0 bg-red-500/50 flex items-center justify-center text-[9px] text-white text-center px-1"
          :title="u.error"
        >
          Failed
        </div>

        <!-- Remove button (always visible) -->
        <button
          type="button"
          class="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500 transition-colors"
          :aria-label="'Remove attachment'"
          @click="removeUpload(u.clientFileId)"
        >
          <el-icon size="10"><Close /></el-icon>
        </button>
      </div>
    </div>

    <div class="flex items-end gap-2 bg-black/30 rounded-lg p-2 border border-white/5 focus-within:border-primary/50 transition-colors">
      <!-- Hidden file input -->
      <input
        ref="fileInputEl"
        type="file"
        class="hidden"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        @change="onFilesPicked"
      />
      <button
        type="button"
        class="shrink-0 p-1.5 rounded text-text-muted hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
        :title="$t('chat.composer.upload_image')"
        :aria-label="$t('chat.composer.upload_image')"
        :disabled="disabled || muted"
        @click="fileInputEl?.click()"
      >
        <el-icon size="14"><Picture /></el-icon>
      </button>

      <button
        type="button"
        class="shrink-0 p-1.5 rounded text-text-muted hover:text-white hover:bg-white/5 transition-colors disabled:opacity-40"
        :title="$t('chat.composer.emoji')"
        :aria-label="$t('chat.composer.emoji')"
        :disabled="disabled || muted"
        @click.stop="emojiPickerOpen = !emojiPickerOpen"
      >
        <span class="inline-block w-[14px] h-[14px] leading-[14px] text-center text-[13px]">😊</span>
      </button>

      <!-- Share music — opens the release/track/artist picker. Tenant-
           scoped catalogue, click-to-insert. The card itself renders
           client-side via URL detection in ChatMessageItem. -->
      <button
        type="button"
        class="shrink-0 p-1.5 rounded text-text-muted hover:text-primary hover:bg-white/5 transition-colors disabled:opacity-40"
        :title="$t('chat.composer.share_music')"
        :aria-label="$t('chat.composer.share_music')"
        :disabled="disabled || muted"
        @click.stop="sharePickerOpen = true"
      >
        <el-icon size="14"><Headset /></el-icon>
      </button>

      <textarea
        ref="textareaEl"
        v-model="text"
        :placeholder="muted ? 'You are muted' : placeholder"
        :disabled="disabled || muted"
        rows="1"
        class="flex-1 bg-transparent text-sm text-text-main placeholder:text-text-dim outline-none resize-none max-h-32 py-1"
        @keydown.enter="onEnter"
        @input="autosize"
        @paste="onPaste"
        @blur="onBlur"
      />
      <button
        type="button"
        class="shrink-0 px-3 py-1.5 rounded-md bg-primary text-black text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
        :disabled="!canSend"
        @click="submit"
      >
        {{ sending ? $t('chat.composer.sending') : $t('chat.composer.send') }}
      </button>
    </div>
    <p v-if="!disabled" class="text-[9px] text-text-dim px-2 pt-1">{{ $t('chat.composer.newline_hint') }}</p>

    <!-- Emoji picker popover — positioned against this composer's root via
         the `relative` class on the outer div. Mounted inline (not teleported)
         so click-outside and focus handling stay local. -->
    <ChatEmojiPicker v-model="emojiPickerOpen" @pick="onEmojiPick" />

    <!-- Music share picker — el-dialog, teleports to body. Selection
         appends an absolute asset URL to the message; the renderer in
         ChatMessageItem detects that pattern and substitutes a rich
         ChatShareCard preview without storing extra schema. -->
    <ChatSharePicker v-model="sharePickerOpen" @select="onShareSelect" />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { Picture, Close, Headset } from '@element-plus/icons-vue'
import { useChatUpload } from '~/composables/chat/useChatUpload'
import { useChatModeration } from '~/composables/chat/useChatModeration'
import { useTypingIndicator } from '~/composables/chat/useTypingIndicator'

const props = defineProps({
  sending: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: '' },
  // Set by the parent (thread view / global room) so composer can emit
  // typing.start/stop frames. Omit to disable typing indicator.
  threadId: { type: String, default: '' },
  threadKind: { type: String, default: 'dm' },    // 'dm' | 'group' | 'label' | 'global'
  // Active reply target — when set, renders the quote preview chip
  // above the composer + sends `replyTo` along with submit. Parent
  // clears via the `cancel-reply` emit.
  replyTo: { type: Object, default: null },
  // Display-name of the current user — used by /me slash command to
  // build the action sentence locally so the composer stays decoupled
  // from useUserSession.
  authorName: { type: String, default: '' }
})
const emit = defineEmits(['send', 'cancel-reply'])

const text = ref('')
const textareaEl = ref(null)
const fileInputEl = ref(null)

const { uploads, uploadFile, remove: removeUpload, clear: clearUploads, toAttachments } = useChatUpload()
const uploadList = computed(() => [...uploads.values()])

const { myMute, isMuted } = useChatModeration()

/* ─────────── Emoji picker ─────────── */

const emojiPickerOpen = ref(false)

/* ─────────── Music share picker ─────────── */

const sharePickerOpen = ref(false)

/**
 * Insert an absolute URL pointing at the picked release/track/artist into
 * the composer text. The URL pattern is what ChatMessageItem detects to
 * swap in the rich ChatShareCard preview, so the wire format stays plain
 * text — no message-schema change, no server-side migration.
 *
 * Uses `window.location.origin` so the receiver in any tenant gets a URL
 * that points back to THIS tenant's domain (matching how a regular paste
 * of a release page link would behave).
 */
function onShareSelect(item) {
  const path = item.type === 'release'
    ? `/releases/${item.id}`
    : item.type === 'track'
      ? `/tracks/${item.id}`
      : `/artists/${item.id}`
  const url = (typeof window !== 'undefined' ? window.location.origin : '') + path
  const insert = (text.value && !text.value.endsWith(' ') ? ' ' : '') + url + ' '
  text.value += insert
  nextTick().then(() => {
    textareaEl.value?.focus()
    autosize()
  })
}

/**
 * Insert a `:name:` token at the current caret position. Falls back to
 * appending at the end when the textarea doesn't have a selection (e.g.
 * when the picker was opened by a shortcut and the composer never had focus).
 */
function onEmojiPick({ name }) {
  const el = textareaEl.value
  const token = `:${name}: `
  if (!el) {
    text.value += token
    return
  }
  const start = el.selectionStart ?? text.value.length
  const end = el.selectionEnd ?? text.value.length
  const before = text.value.slice(0, start)
  const after = text.value.slice(end)
  text.value = before + token + after
  nextTick().then(() => {
    el.focus()
    const caret = before.length + token.length
    el.setSelectionRange(caret, caret)
    autosize()
  })
}

// Tick every 10s so the countdown stays roughly fresh without burning CPU.
// Only run the interval while the user is actually muted — otherwise the
// composer wastes a setInterval for the entire chat session.
const now = ref(Date.now())
let tickInterval = null
function startTick() {
  if (tickInterval) return
  tickInterval = setInterval(() => { now.value = Date.now() }, 10_000)
}
function stopTick() {
  if (tickInterval) { clearInterval(tickInterval); tickInterval = null }
}
onBeforeUnmount(() => stopTick())

const muted = computed(() => {
  // Re-evaluate when `now` changes so the banner clears when the mute expires
  void now.value
  return isMuted()
})

// Gate the countdown tick on actual mute state so we don't burn cycles for
// users who'll never see the banner.
watch(muted, (isM) => {
  if (isM) startTick()
  else stopTick()
}, { immediate: true })

/** Short human label for the active mute: "for 42m" / "indefinitely" / "" */
const muteDurationLabel = computed(() => {
  const m = myMute.value
  if (!m) return ''
  if (!m.until) return ' indefinitely'
  const diffMs = new Date(m.until).getTime() - now.value
  if (diffMs <= 0) return ''
  const mins = Math.ceil(diffMs / 60_000)
  if (mins < 60) return ` for ${mins}m`
  const hrs = Math.ceil(mins / 60)
  if (hrs < 24) return ` for ${hrs}h`
  const days = Math.ceil(hrs / 24)
  return ` for ${days}d`
})

const anyPending = computed(() =>
  uploadList.value.some(u => u.state === 'uploading' || u.state === 'queued')
)

const canSend = computed(() => {
  if (props.disabled || props.sending) return false
  if (muted.value) return false
  if (anyPending.value) return false
  const hasText = text.value.trim().length > 0
  const hasReadyAttachments = uploadList.value.some(u => u.state === 'done')
  return hasText || hasReadyAttachments
})

const { notifyTyping, stopTyping } = useTypingIndicator()

function autosize() {
  const el = textareaEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(128, el.scrollHeight) + 'px'
  // Fire typing frame on keystroke (rate-limited internally)
  if (props.threadId && text.value.length > 0) {
    notifyTyping(props.threadId, props.threadKind)
  }
}

function onEnter(e) {
  if (e.shiftKey) return
  // IME guard: when CJK / Korean composition is active, Enter commits
  // the candidate — must NOT trigger send. keyCode 229 is Safari's
  // pre-isComposing fallback.
  if (e.isComposing || e.keyCode === 229) return
  e.preventDefault()
  submit()
}

// When the textarea loses focus, clear the typing indicator so the peer's
// "X is typing…" pill doesn't linger after the user navigates away from
// the composer (e.g. opened the friends list mid-draft).
function onBlur() {
  if (props.threadId) stopTyping(props.threadId, props.threadKind)
}

async function onFilesPicked(e) {
  const files = Array.from(e.target.files || [])
  // Clear so selecting the same file again re-triggers change
  e.target.value = ''
  for (const f of files) await uploadFile(f)
}

/** Support pasting images directly into the composer. */
async function onPaste(e) {
  if (!e.clipboardData) return
  const items = Array.from(e.clipboardData.items || [])
  const imgs = items.filter(it => it.type && it.type.startsWith('image/'))
  if (imgs.length === 0) return
  e.preventDefault()
  for (const it of imgs) {
    const file = it.getAsFile()
    if (file) await uploadFile(file)
  }
}

async function submit() {
  if (!canSend.value) return
  let body = text.value.trim()

  // Slash command handling — applied locally before sending. Server
  // sees only the resulting plain text. Supported commands:
  //   /me action    → "<author> action"
  //   /shrug [text] → "[text] ¯\\_(ツ)_/¯"
  //   /share        → opens the music share picker (no message sent)
  if (body.startsWith('/')) {
    const lower = body.toLowerCase()
    if (lower === '/share' || lower.startsWith('/share ')) {
      // Open the share picker instead of sending. Composer text stays so
      // the user can complete their message after picking.
      text.value = body.replace(/^\/share\s*/i, '')
      sharePickerOpen.value = true
      return
    }
    const meName = props.authorName || ''
    if (lower.startsWith('/me ') && body.length > 4) {
      const rest = body.slice(4).trim()
      body = (meName ? `${meName} ` : '') + rest
    } else if (lower === '/shrug') {
      body = `¯\\_(ツ)_/¯`
    } else if (lower.startsWith('/shrug ') && body.length > 7) {
      body = body.slice(7).trim() + ' ¯\\_(ツ)_/¯'
    }
  }

  const attachments = toAttachments()
  text.value = ''
  clearUploads()
  // Explicitly stop typing on send so the recipient's indicator clears instantly
  if (props.threadId) stopTyping(props.threadId, props.threadKind)
  await nextTick()
  autosize()
  // `replyTo.id` lets the parent forward to the server; the parent is
  // also responsible for clearing the reply state on success.
  emit('send', { body, attachments, replyTo: props.replyTo?.id || null })
}

// Ensure typing indicator clears when the composer is unmounted (thread switch)
onBeforeUnmount(() => {
  if (props.threadId) stopTyping(props.threadId, props.threadKind)
})

function focus() { textareaEl.value?.focus() }

defineExpose({ focus })
</script>
